import { enhancedApi as generatedBillingV2Api } from '@store/generated/billing-v2';

// Re-export all generated types for convenience
export type {
  AccountActivityRowView,
  AccountActivitySourceView,
  ActivityFeedEvent,
  ActivityFeedGroup,
  AssignChargeToEncounterRequest,
  AssignChargeToEncounterResponse,
  AttentionDisposition,
  AttentionLane,
  BillingOverviewResponse,
  BillingPayerOptionRow,
  BillingPayerOptionsResponse,
  BillingSavedView,
  BillingSavedViewListResponse,
  ChargeLineItem,
  ChargeLinePreviewEncounter,
  ChargeLinePreviewLine,
  ChargeLinePreviewRequest,
  ChargeLinePreviewResponse,
  ClaimCase,
  ClaimCaseDetailResponse,
  ClaimCaseListItem,
  ClaimCaseListResponse,
  ClaimCaseReviewDetail,
  ClaimCaseReviewResult,
  ClaimCertificationResponse as ClaimCertification,
  CertificationCountSummary as ClaimCertificationCountSummary,
  CertificationDispositionItem as ClaimCertificationDisposition,
  DispositionCommandRequest as ClaimCertificationDispositionCommand,
  DispositionCommandResponse as ClaimCertificationDispositionCommandResult,
  DispositionConflictResponse as ClaimCertificationDispositionConflict,
  CertificationFindingItem as ClaimCertificationFinding,
  CertificationFindingAnchorItem as ClaimCertificationFindingAnchor,
  CertificationFindingEffectItem as ClaimCertificationFindingEffect,
  CertificationWarning as ClaimCertificationWarning,
  ClaimsV2Response,
  ClaimV2Item,
  ClaimWorkCounts,
  ClaimWorkFilters,
  ClaimWorkFindingHighlight,
  ClaimWorkItem,
  ClaimWorkListResponse,
  ClaimWorkSavedConfig,
  ClaimWorkSortKey,
  ClaimWorkViewKey,
  CreditAllocationRequest,
  CreditAllocationResponse,
  EncounterAdjustmentsSplit,
  EncounterClaimSummary,
  EncounterLedgerGroup,
  EraTriage,
  FlowHealthRow,
  GenerateClaimCaseReviewResponse,
  LedgerEntryItem,
  PatientBillingQueueResponse,
  PatientBillingQueueSummary,
  PatientBillingStatus,
  PatientDueStatus,
  PatientFinancialSummaryResponse,
  PatientFinancialSummaryTotals,
  PatientInvoiceHistoryItem,
  PatientQueueItem,
  PatientRefundRequest,
  PatientRefundResponse,
  PatientResponsibilityBreakdown,
  RefundablePaymentItem,
  RefundablePaymentsResponse,
  RemittancesV2Response,
  RemittanceV2Item,
  SegmentCount,
  SentInvoiceItem,
  SentInvoicesResponse,
  SentInvoicesSummary,
  SummaryStrip,
  WhatToDo,
  WorkGroup,
  WorkItem,
  WorkQueueResponse,
} from '@store/generated/billing-v2';

// Cache tag constants
const V2_TAGS = {
  OVERVIEW: 'BillingV2Overview',
  WORK: 'BillingV2Work',
  CLAIMS: 'BillingV2Claims',
  CLAIM_CASES: 'BillingV2ClaimCases',
  REMITTANCES: 'BillingV2Remittances',
  PATIENT_BILLING: 'BillingV2PatientBilling',
  PATIENT_DETAIL: 'BillingV2PatientDetail',
  CLAIM_CERTIFICATION: 'BillingV2ClaimCertification',
  CLAIM_WORK: 'BillingV2ClaimWork',
  CLAIM_WORK_SAVED_VIEWS: 'BillingV2ClaimWorkSavedViews',
} as const;

// Enhance generated API with cache tags
const billingV2Api = generatedBillingV2Api.enhanceEndpoints({
  addTagTypes: Object.values(V2_TAGS),
  endpoints: {
    getV2ProviderBillingV2Overview: {
      providesTags: [V2_TAGS.OVERVIEW],
    },
    getV2ProviderBillingV2Work: {
      providesTags: [V2_TAGS.WORK],
    },
    getV2ProviderBillingV2Claims: {
      providesTags: [V2_TAGS.CLAIMS],
    },
    getV2ProviderBillingV2ClaimWork: {
      providesTags: [V2_TAGS.CLAIM_WORK],
    },
    getV2ProviderBillingV2ClaimWorkSavedViews: {
      providesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    postV2ProviderBillingV2ClaimWorkSavedViews: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    putV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewId: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    deleteV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewId: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    getV2ProviderBillingV2ClaimCases: {
      providesTags: [V2_TAGS.CLAIM_CASES],
    },
    getV2ProviderBillingV2ClaimCasesByClaimId: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    postV2ProviderBillingV2ClaimCasesByClaimIdReview: {
      invalidatesTags: (_result, _err, arg) => [
        V2_TAGS.CLAIM_CASES,
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    getV2ProviderBillingV2Remittances: {
      providesTags: [V2_TAGS.REMITTANCES],
    },
    getV2ProviderBillingV2PatientBillingQueue: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientBillingSent: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientsByPatientIdFinancialSummary: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: arg.patientId },
      ],
    },
    postV2ProviderChargeLinesPreview: {
      // Read-only preview — no cache invalidation. The mutation shape is a BE
      // choice (POST so the long ID list goes in the body); we treat it as a
      // query in the wrapper hook.
    },
    getV2ProviderClaimsByClaimIdCertification: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId },
      ],
    },
    postV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyDismiss: {
      invalidatesTags: (result, _err, arg) =>
        result ? [{ type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId }] : [],
    },
    postV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyReopen: {
      invalidatesTags: (result, _err, arg) =>
        result ? [{ type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId }] : [],
    },
  },
});

// The money verbs (gdn-2950 allocation, gdn-2978 refund) come from codegen;
// only the account-history PDF (gdn-2942) is hand-injected, because a Blob
// response has no OpenAPI shape to generate from.
//
// Every money verb refreshes the same billing surfaces the reverse-charge
// verb does (see api.ts), plus this patient's financial summary. One
// over-broad refetch beats a stale balance.
const moneyVerbInvalidation = (patientId: string) => [
  { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
  V2_TAGS.PATIENT_BILLING,
  V2_TAGS.OVERVIEW,
  { type: 'PatientBalance' as const },
  { type: 'PatientInvoices' as const },
  { type: 'BillingEncounter' as const },
  { type: 'BillingEncountersList' as const, id: 'LIST' },
  { type: 'Eligibility' as const },
  { type: 'Worklist' as const },
];

const billingV2ApiWithDocuments = billingV2Api
  .enhanceEndpoints({
    // Cross-slice tags owned by api.ts — re-declared here so this chain's
    // types know them (runtime registration is shared and idempotent).
    addTagTypes: [
      'PatientBalance',
      'PatientInvoices',
      'BillingEncounter',
      'BillingEncountersList',
      'Eligibility',
      'Worklist',
    ],
    endpoints: {
      postV2ProviderBillingV2PatientsByPatientIdCreditAllocations: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      // The assignment voids an account charge AND lands credit on an
      // encounter, so it moves money across both scopes — same blast
      // radius as the other money verbs (gdn-3106).
      postV2ProviderBillingV2PatientsByPatientIdChargeAssignments: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      postV2ProviderBillingV2PatientsByPatientIdRefunds: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      getV2ProviderBillingV2PatientsByPatientIdRefundablePayments: {
        // Rides the patient-detail tag so any money verb refreshes it.
        providesTags: (_result, _error, { patientId }) => [
          { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      getPatientAccountHistoryPdf: builder.query<Blob, { patientId: string }>({
        query: ({ patientId }) => ({
          url: `/v2/provider/billing/v2/patients/${patientId}/account-history`,
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),
    }),
    overrideExisting: false,
  });

export const {
  useLazyGetPatientAccountHistoryPdfQuery: useLazyAccountHistoryPdfQuery,
  usePostV2ProviderBillingV2PatientsByPatientIdCreditAllocationsMutation:
    useCreditAllocationMutation,
  usePostV2ProviderBillingV2PatientsByPatientIdChargeAssignmentsMutation:
    useAssignChargeToEncounterMutation,
  useGetV2ProviderBillingV2PatientsByPatientIdRefundablePaymentsQuery:
    useRefundablePaymentsQuery,
  usePostV2ProviderBillingV2PatientsByPatientIdRefundsMutation:
    usePatientRefundMutation,
} = billingV2ApiWithDocuments;

// Export user-friendly hooks
export const {
  useGetV2ProviderBillingV2OverviewQuery: useOverviewQuery,
  useGetV2ProviderBillingV2WorkQuery: useWorkQueueQuery,
  useGetV2ProviderBillingV2ClaimsQuery: useClaimsV2Query,
  useGetV2ProviderBillingV2ClaimWorkQuery: useClaimWorkQuery,
  useGetV2ProviderBillingV2ClaimWorkSavedViewsQuery: useClaimWorkSavedViewsQuery,
  usePostV2ProviderBillingV2ClaimWorkSavedViewsMutation: useCreateClaimWorkSavedViewMutation,
  usePutV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewIdMutation:
    useUpdateClaimWorkSavedViewMutation,
  useDeleteV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewIdMutation:
    useDeleteClaimWorkSavedViewMutation,
  useGetV2ProviderBillingV2ClaimCasesQuery: useClaimCasesQuery,
  useGetV2ProviderBillingV2ClaimCasesByClaimIdQuery: useClaimCaseDetailQuery,
  usePostV2ProviderBillingV2ClaimCasesByClaimIdReviewMutation: useGenerateClaimCaseReviewMutation,
  useGetV2ProviderBillingV2RemittancesQuery: useRemittancesV2Query,
  useGetV2ProviderBillingV2PatientBillingQueueQuery: usePatientBillingQueueQuery,
  useGetV2ProviderBillingV2PatientBillingSentQuery: usePatientBillingSentQuery,
  useGetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryQuery: usePatientFinancialSummaryQuery,
  usePostV2ProviderChargeLinesPreviewMutation: useChargeLinesPreviewMutation,
  useGetV2ProviderBillingV2PayerOptionsQuery: useBillingPayerOptionsQuery,
  useGetV2ProviderClaimsByClaimIdCertificationQuery: useClaimCertificationQuery,
  usePostV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyDismissMutation:
    useDismissClaimCertificationFindingMutation,
  usePostV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyReopenMutation:
    useReopenClaimCertificationFindingMutation,
} = billingV2Api;

export { billingV2Api, V2_TAGS };
